import {
  CreateAssessment,
  Assessment,
  AssessmentMin,
  AssessmentId,
  RiskMin,
  FilterAssessments,
  OwnerId,
  AssessmentStatus,
  TprmReportTemplateId,
  CreateRisk,
  RiskId,
  PendingBackgroundTask,
  AssessmentNode,
  CreateAssessmentDecision,
  ThirdPartyId,
} from "../../Types";
import jsonApi, {addAccountId} from "../jsonApi";
import {invalidateQueries, q} from "../../state";
import {CURRENT_REGION} from "../../utils/region";

export async function search(filter: FilterAssessments): Promise<Assessment[]> {
  return await jsonApi.post<Assessment[]>(`/region/${CURRENT_REGION}/api/tprm/assessments/search`, filter);
}

export async function get(assessmentId: AssessmentId): Promise<Assessment> {
  return await jsonApi.get<Assessment>(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}`);
}

export async function delete_(assessmentId: AssessmentId): Promise<void> {
  await jsonApi.delete_<Assessment>(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}`);
  await invalidateQueries([q.tprm.thirdPartyAssessments.filter(), q.tprm.assessment.filter(assessmentId)]);
}

export async function create(
  /**
   * The third party ID is not strictly needed to create the assessment, which
   * is why it's not in `CreateAssessment`. However, we provide it here so we can
   * invalidate some third party queries.
   */
  thirdPartyId: ThirdPartyId,
  assessment: CreateAssessment,
): Promise<Assessment> {
  const res = await jsonApi.post<Assessment>(`/region/${CURRENT_REGION}/api/tprm/assessments`, assessment);
  await invalidateQueries([
    // `num_assessments` field on ThirdParty and ThirdPartyMin
    q.tprm.thirdParties.filter(),
    q.tprm.thirdParty.filter(thirdPartyId),
    // Assessments
    q.tprm.thirdPartyAssessments.filter(thirdPartyId),
    q.tprm.assessmentsWithQuestionnaires.filter(),
  ]);
  return res;
}

export async function advance(assessmentId: AssessmentId): Promise<"ok"> {
  const res = await jsonApi.post<"ok">(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/advance`);
  await invalidateQueries([
    q.tprm.thirdPartyAssessments.filter(),
    q.tprm.assessment.filter(),
    q.tprm.assessmentsWithQuestionnaires.filter(),
  ]);
  return res;
}

export async function startAgentSession(assessmentId: AssessmentId): Promise<"ok"> {
  const res = await jsonApi.post<"ok">(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/agent_session`);
  await invalidateQueries([
    q.tprm.thirdPartyAssessments.filter(),
    q.tprm.assessment.filter(),
    q.tprm.assessmentsWithQuestionnaires.filter(),
  ]);
  return res;
}

export async function autoSuggest(assessmentId: AssessmentId): Promise<"ok"> {
  const res = await jsonApi.post<"ok">(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/auto_suggest`);
  await invalidateQueries([
    q.tprm.thirdPartyAssessments.filter(),
    q.tprm.assessment.filter(),
    q.tprm.assessmentsWithQuestionnaires.filter(),
  ]);
  return res;
}

export async function createRisk(assessmentId: AssessmentId, risk: CreateRisk): Promise<RiskId> {
  const res = await jsonApi.post<RiskId>(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/risks`, risk);
  await invalidateQueries([q.tprm.assessmentRisks.filter(assessmentId), q.tprm.assessmentNodes.filter(assessmentId)]);
  return res;
}

export async function listRisks(assessmentId: AssessmentId): Promise<RiskMin[]> {
  return await jsonApi.get<RiskMin[]>(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/risks`);
}

export async function assign(assessmentId: AssessmentId, owner: OwnerId | null): Promise<"ok"> {
  const res = await jsonApi.post<"ok">(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/owner`, owner);
  await invalidateQueries([
    q.tprm.thirdPartyAssessments.filter(),
    q.tprm.assessmentsWithQuestionnaires.filter(),
    q.tprm.assessment.filter(assessmentId),
  ]);
  return res;
}

export async function updateStatus(assessmentId: AssessmentId, status: AssessmentStatus): Promise<"ok"> {
  const res = await jsonApi.post<"ok">(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/status`, status);
  await invalidateQueries([
    q.tprm.thirdPartyAssessments.filter(),
    q.tprm.assessmentsWithQuestionnaires.filter(),
    q.tprm.assessment.filter(assessmentId),
  ]);
  return res;
}

export async function updateExternalId(assessmentId: AssessmentId, externalId: string): Promise<string> {
  const res = await jsonApi.post<"ok">(
    `/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/external_id`,
    externalId,
  );
  await invalidateQueries([q.tprm.assessmentsWithQuestionnaires.filter(), q.tprm.assessment.filter(assessmentId)]);
  return res;
}

export async function listNodes(assessmentId: AssessmentId): Promise<AssessmentNode[]> {
  return await jsonApi.get<AssessmentNode[]>(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/nodes`);
}

export async function bulkDecide(assessmentId: AssessmentId, decisions: CreateAssessmentDecision[]): Promise<"ok"> {
  const res = await jsonApi.post<"ok">(
    `/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/decisions`,
    decisions,
  );
  await invalidateQueries([
    q.tprm.assessment.filter(assessmentId),
    q.tprm.assessmentRisks.filter(assessmentId),
    q.tprm.assessmentNodes.filter(assessmentId),
    q.tprm.assessedControl.filter(assessmentId),
    q.tprm.risk.filter(assessmentId),
  ]);
  return res;
}

export const downloadReport = async (assessmentId: AssessmentId, reportTemplateId: TprmReportTemplateId) => {
  const a = document.createElement("a");
  a.href = addAccountId(
    `/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/report/${reportTemplateId}/download`,
  );
  a.click();
};

export const getReportUrl = (assessmentId: AssessmentId, reportTemplateId: TprmReportTemplateId) => {
  return addAccountId(`/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/report/${reportTemplateId}`);
};

export const exportAssessment = async (assessmentId: AssessmentId) => {
  const res = jsonApi.post<PendingBackgroundTask>(
    `/region/${CURRENT_REGION}/api/tprm/assessments/${assessmentId}/export`,
  );
  return res;
};

export const list = async () => {
  return await jsonApi.get<AssessmentMin[]>(`/region/${CURRENT_REGION}/api/tprm/assessments`);
};

export const listWithQuestionnaires = async () => {
  return await jsonApi.get<AssessmentMin[]>(`/region/${CURRENT_REGION}/api/tprm/assessments/with_questionnaires`);
};
